import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { Button } from '@components/common'
import { useProjectWizardStore } from '@store/useProjectWizardStore'
import authService from '@services/authService'
import projectService from '@services/projectService'
import projectDocumentService from '@services/projectDocumentService'
import checklistService from '@services/checklistService'
import WizardProgress from './WizardProgress'
import BasicDetailsStep from './steps/BasicDetailsStep'
import ProjectScopeStep from './steps/ProjectScopeStep'
import DocumentUploadStep from './steps/DocumentUploadStep'
import ReviewStep from './steps/ReviewStep'
import ChecklistStep from './steps/ChecklistStep'
import './ProjectWizard.scss'

/**
 * ProjectWizard Component
 *
 * Multi-step wizard for creating new compliance projects
 */
const ProjectWizard = ({ onComplete, onCancel }) => {
	const navigate = useNavigate()

	const {
		currentStep,
		nextStep,
		prevStep,
		goToStep,
		basicDetails,
		projectScope,
		checklistType,
		documents,
		checklist,
		createdProject,
		isSubmitting,
		error,
		setIsSubmitting,
		setError,
		setCreatedProject,
		setChecklist,
		setIsGenerating,
		setGenerationError,
		reset,
	} = useProjectWizardStore()

	const [stepValidity, setStepValidity] = useState({
		1: false,
		2: false,
		3: true, // Documents optional
		4: false,
		5: false,
	})

	// Wizard steps configuration
	const aiSteps = [
		{ id: 'basic', title: 'Basic Details', subtitle: 'Name & Standards' },
		{ id: 'scope', title: 'Project Scope', subtitle: 'Context & Team' },
		{ id: 'documents', title: 'Documents', subtitle: 'Upload Files' },
		{ id: 'review', title: 'Review', subtitle: 'Confirm Details' },
		{ id: 'checklist', title: 'Checklist', subtitle: 'AI Generation' },
	]
	const manualSteps = [
		{ id: 'basic', title: 'Basic Details', subtitle: 'Name & Standards' },
		{ id: 'scope', title: 'Project Scope', subtitle: 'Context & Team' },
		{ id: 'checklist', title: 'Checklist', subtitle: 'Manual Entry' },
	]
	const steps = checklistType === 'manual' ? manualSteps : aiSteps

	// In manual mode, step 5 displays as step 3 in the progress bar
	const displayStep = checklistType === 'manual' && currentStep === 5 ? 3 : currentStep

	// Handle step validity change - memoized to prevent infinite loops
	const handleValidChange = useCallback((isValid) => {
		setStepValidity((prev) => {
			if (prev[currentStep] === isValid) return prev
			return { ...prev, [currentStep]: isValid }
		})
	}, [currentStep])

	// Handle navigation to specific step (display step index → actual step)
	const handleStepClick = (displayStepNum) => {
		if (checklistType === 'manual') {
			const actualStep = displayStepNum <= 2 ? displayStepNum : 5
			if (actualStep < currentStep) goToStep(actualStep)
		} else {
			if (displayStepNum < currentStep) goToStep(displayStepNum)
		}
	}

	// Handle edit from review step
	const handleEditFromReview = (step) => {
		goToStep(step)
	}

	// Create project and generate checklist
	const handleCreateAndGenerate = async () => {
		setIsSubmitting(true)
		setError(null)

		try {
			const user = authService.getCurrentUser()
			if (!user) {
				throw new Error('User not authenticated')
			}

			// Prepare project data
			// SECURITY: company_id is automatically set by backend from authenticated user
			const projectData = {
				created_by: user.id,
				name: basicDetails.name,
				description: basicDetails.description,
				certificates: basicDetails.certificates,
				industry: projectScope.industry,
				domain: projectScope.domain,
				technology_stack: projectScope.technologyStack,
				geographical_locations: projectScope.geographicalLocations,
				team_roles: projectScope.teamRoles,
				associatedRoles: projectScope.associatedRoles || [], // Role IDs for workflow creation (users assigned later)
				start_date: projectScope.startDate || null,
				target_date: projectScope.targetDate || null,
				status: 'planning',
				progress: 0,
			}

			// Create project
			const projectResponse = await projectService.create(projectData)
			const project = projectResponse.data

			setCreatedProject(project)

			// Upload documents if any
			if (documents.length > 0) {
				const uploadStore = useProjectWizardStore.getState()

				for (const doc of documents) {
					try {
						uploadStore.setUploadProgress(doc.id, 0)

						await projectDocumentService.upload(
							doc.file,
							{
								projectId: project.id,
								documentType: doc.type,
								uploadedBy: user.id,
							},
							(progress) => {
								uploadStore.setUploadProgress(doc.id, progress)
							}
						)

						uploadStore.updateDocument(doc.id, { status: 'uploaded' })
						uploadStore.clearUploadProgress(doc.id)
					} catch (uploadError) {
						console.error('Failed to upload document:', uploadError)
						uploadStore.updateDocument(doc.id, { status: 'error' })
					}
				}
			}

			// Move to checklist step
			nextStep()

			// Generate AI checklist
			setIsGenerating(true)
			setGenerationError(null)

			try {
				const checklistResponse = await projectService.generateChecklist(project.id)
				// Ensure checklist data is always an array
				const checklistData = checklistResponse?.data
				const checklistArray = Array.isArray(checklistData) ? checklistData : []
				setChecklist(checklistArray)
			} catch (genError) {
				console.error('Checklist generation failed:', genError)
				setGenerationError(genError.message || 'Failed to generate checklist')
			} finally {
				setIsGenerating(false)
			}
		} catch (err) {
			console.error('Project creation failed:', err)
			setError(err.message || 'Failed to create project')
		} finally {
			setIsSubmitting(false)
		}
	}

	// Finalize and save checklist
	const handleFinalize = async () => {
		setIsSubmitting(true)
		setError(null)

		try {
			let project = createdProject

			// In manual mode the project hasn't been created yet — create it now
			if (!project) {
				const user = authService.getCurrentUser()
				if (!user) throw new Error('User not authenticated')

				const projectData = {
					created_by: user.id,
					name: basicDetails.name,
					description: basicDetails.description,
					certificates: basicDetails.certificates,
					industry: projectScope.industry,
					domain: projectScope.domain,
					technology_stack: projectScope.technologyStack,
					geographical_locations: projectScope.geographicalLocations,
					team_roles: projectScope.teamRoles,
					associatedRoles: projectScope.associatedRoles || [],
					start_date: projectScope.startDate || null,
					target_date: projectScope.targetDate || null,
					status: 'planning',
					progress: 0,
				}

				const projectResponse = await projectService.create(projectData)
				project = projectResponse.data
				setCreatedProject(project)
			}

			// Save checklist items (batch update in case of edits)
			if (checklist.length > 0) {
				// For items that already have IDs (from AI generation), update them
				// For new items (temp IDs), create them
				const existingItems = checklist.filter((item) => !String(item.id).startsWith('temp_'))
				const newItems = checklist.filter((item) => String(item.id).startsWith('temp_'))

				if (existingItems.length > 0) {
					await checklistService.batchUpdate(existingItems)
				}

				if (newItems.length > 0) {
					// Remove temp IDs before creating
					const itemsToCreate = newItems.map(({ id, ...rest }) => rest)
					await checklistService.batchCreate(project.id, itemsToCreate)
				}
			}

			// Call completion callback or navigate
			if (onComplete) {
				onComplete(project)
			} else {
				navigate(`/projects/${project.id}`)
			}

			// Reset wizard state
			reset()
		} catch (err) {
			console.error('Finalization failed:', err)
			setError(err.message || 'Failed to save project')
		} finally {
			setIsSubmitting(false)
		}
	}

	// Handle cancel
	const handleCancel = () => {
		if (window.confirm('Are you sure you want to cancel? All entered data will be lost.')) {
			reset()
			if (onCancel) {
				onCancel()
			} else {
				navigate('/dashboard')
			}
		}
	}

	// Render current step
	const renderStep = () => {
		switch (currentStep) {
			case 1:
				return <BasicDetailsStep onValidChange={handleValidChange} />
			case 2:
				return <ProjectScopeStep onValidChange={handleValidChange} />
			case 3:
				return <DocumentUploadStep onValidChange={handleValidChange} />
			case 4:
				return <ReviewStep onValidChange={handleValidChange} onEdit={handleEditFromReview} />
			case 5:
				return <ChecklistStep onValidChange={handleValidChange} isManual={checklistType === 'manual'} />
			default:
				return null
		}
	}

	// Check if can proceed
	const canProceed = stepValidity[currentStep]

	// Render the back button
	const renderBackButton = () => {
		// AI mode: no back on step 5; Manual mode: back on step 5 goes to step 2
		const showBack = currentStep > 1 && (currentStep !== 5 || checklistType === 'manual')
		if (!showBack) return null

		const handleBack = () => {
			if (checklistType === 'manual' && currentStep === 5) {
				goToStep(2)
			} else {
				prevStep()
			}
		}

		return (
			<Button variant="outline" onClick={handleBack} disabled={isSubmitting}>
				<i className="icon-arrow-left" /> Back
			</Button>
		)
	}

	// Render the next/action button
	const renderNextButton = () => {
		if (currentStep === 5) {
			return (
				<Button
					variant="primary"
					onClick={handleFinalize}
					disabled={checklist.length === 0 || isSubmitting}
				>
					{isSubmitting ? 'Saving...' : 'Save & Finalize'}
				</Button>
			)
		}

		if (currentStep === 4) {
			return (
				<Button
					variant="primary"
					onClick={handleCreateAndGenerate}
					disabled={!canProceed || isSubmitting}
				>
					{isSubmitting ? 'Creating...' : 'Create & Generate Checklist'}
				</Button>
			)
		}

		// Step 2 in manual mode: skip to checklist (step 5)
		if (currentStep === 2 && checklistType === 'manual') {
			return (
				<Button
					variant="primary"
					onClick={() => goToStep(5)}
					disabled={!canProceed}
				>
					Continue <i className="icon-arrow-right" />
				</Button>
			)
		}

		// Steps 1–3 (AI mode) or step 1 (manual mode)
		if (currentStep < 4) {
			return (
				<Button
					variant="primary"
					onClick={nextStep}
					disabled={!canProceed}
				>
					Continue <i className="icon-arrow-right" />
				</Button>
			)
		}

		return null
	}

	return (
		<div className="project-wizard">
			{/* Header */}
			<div className="project-wizard__header">
				<h1 className="project-wizard__title">Create New Compliance Project</h1>
				<Button variant="ghost" onClick={handleCancel}>
					<i className="icon-close" /> Cancel
				</Button>
			</div>

			{/* Progress */}
			<WizardProgress
				currentStep={displayStep}
				steps={steps}
				onStepClick={handleStepClick}
			/>

			{/* Error Message */}
			{error && (
				<div className="project-wizard__error">
					<i className="icon-ban" /> {error}
				</div>
			)}

			{/* Step Content */}
			<div className="project-wizard__content">{renderStep()}</div>

			{/* Navigation */}
			<div className="project-wizard__footer">
				<div className="project-wizard__nav">
					{renderBackButton()}
					<div className="project-wizard__nav-right">
						{renderNextButton()}
					</div>
				</div>
			</div>
		</div>
	)
}

ProjectWizard.propTypes = {
	onComplete: PropTypes.func,
	onCancel: PropTypes.func,
}

export default ProjectWizard
